<script lang="ts" setup>
import { mdiArrowRight, mdiEmailOutline } from "@mdi/js";
import {
  getTeamColor,
  getTeamIconPath,
  TeamVerticalDefaultProps,
} from "./helper";
import { TypedRouteLocationRawFromName } from "@typed-router";
import { type RoutesNamesList } from "#build/typed-router/__routes";

const { t } = useI18n();
const props = withDefaults(
  defineProps<
    TeamVerticalDefaultProps & {
      link?: string | TypedRouteLocationRawFromName<RoutesNamesList, string>;
      email?: string;
      color?: string;
    }
  >(),
  {
    link: "",
    email: "",
    slug: "",
    title: "",
    subtitle: "",
    iconPath: "",
    cardColor: "",
    textColor: "",
    color: "",
  },
);

const iconPathComputed = computed(() => getTeamIconPath(props));
const isExternalLink = computed(() => typeof props.link === "string");
</script>

<template>
  <v-sheet
    :id="props.slug"
    style="background-color: transparent"
    class="team-vertical-card"
  >
    <div class="team-vertical-card__container mx-auto">
      <h2
        class="text-h2 team-vertical-card__title pt-6 pb-0 text-center text-md-left"
      >
        {{ title }}
      </h2>

      <div class="team-vertical-card__image-wrapper mt-6 mt-lg-0">
        <v-sheet :color="color" class="hw-hexagon team-vertical-card__image">
          <v-responsive
            aspect-ratio="1"
            content-class="d-flex justify-center hw-hexagon"
          >
            <hw-image
              v-if="iconPathComputed"
              :src="iconPathComputed"
              style="max-width: 60%"
              object-fit="contain"
            />
          </v-responsive>
        </v-sheet>
      </div>

      <p
        class="team-vertical-card__subtitle text-center text-md-left mt-6 mt-lg-2 mt-xl-10 mb-4 mb-xl-4"
      >
        {{ subtitle }}
      </p>

      <v-row no-gutters class="team-vertical-card__buttons">
        <v-col
          cols="12"
          sm="6"
          md="5"
          class="d-flex justify-center justify-sm-start"
        >
          <HwButton
            :title="t('navigation.button.contact')"
            :icon="[mdiEmailOutline]"
            underline
            :background="color"
            :href="email"
            class="team-vertical-card--contact-button hw-my-smaller px-8 w-100--xs-only"
            size="dense"
          />
        </v-col>

        <v-col
          cols="12"
          sm="6"
          md="7"
          class="d-flex justify-center justify-sm-end"
        >
          <HwButton
            :title="t('button.learn-more-about', [title])"
            :icon="[mdiArrowRight]"
            underline
            class="hw-mt-smaller"
            :to="isExternalLink ? undefined : link"
            :href="isExternalLink ? link : undefined"
            :external-link="isExternalLink"
            size="dense"
          />
        </v-col>
      </v-row>
    </div>
  </v-sheet>
</template>

<style lang="scss" scoped>
@use "assets/scss/settings";
@use "~/../_common/assets/scss/grid";

.w-100--xs-only {
  @media #{map-get(settings.$display-breakpoints, 'xs')} {
    width: 100%;
  }
}
/** contact and applicationDevelopment and artificial intelligence button texts collide if they stand next to each other in sm**/
.team-vertical-card__buttons .hw-btn-base-text {
  @media (min-width: 600px) and (max-width: 680px) {
    font-size: 85% !important;
  }
}

.team-vertical-card {
  &__container {
    display: grid;
    gap: 0 36px;
    grid-template-areas:
      "title"
      "image"
      "subtitle"
      "buttons";
    grid-template-columns: 1fr;
    @media #{map-get(settings.$display-breakpoints, 'md-and-up')} {
      grid-template-areas:
        "image title"
        "image subtitle"
        "image buttons";
      @include grid.team-vertical-card--grid-layout;
    }
  }

  &__image-wrapper {
    grid-area: image;

    @media #{map-get(settings.$display-breakpoints, 'lg-and-up')} {
      width: 90%;
      justify-self: center;
    }
  }

  &__title {
    grid-area: title;
  }

  &__subtitle {
    grid-area: subtitle;
  }

  &__buttons {
    grid-area: buttons;
  }

  &__image {
    @media #{map-get(settings.$display-breakpoints, 'md-and-down')} {
      width: clamp(150px, 80%, 250px);
      margin: auto !important;
    }
  }
}
</style>
